import { useQuery } from '@apollo/client';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import {
  endpointPortfolioConstituentBreakdownGET,
  endpointPortfolioForwardProjectionGET,
  endpointPortfolioPerformanceGET,
} from '~/api/def/endpoints/portfolioAnalysisEndpoint';
import { AssetBreakdownWidget } from '~/components/ui/widgets/AssetBreakdownWidget/AssetBreakdownWidget';
import { ForwardReturnsWidget } from '~/components/ui/widgets/ForwardReturnsWidget/ForwardReturnsWidget';
import { HistoricalReturnsWidget } from '~/components/ui/widgets/HistoricalReturnsWidget/HistoricalReturnsWidget';
import { GET_LATEST_SNAPSHOT_ID } from '~/graphql/queries/portfolio';
import { useHasBenchmark } from '~/hooks/useHasBenchmark';

type SearchParams = {
  portfolioId?: string;
  snapshotId?: string;
  widget?: string;
  scenario?: 'proposed' | 'current_holdings' | 'benchmark' | 'universe';
  horizonDays?: number;
};

export const Route = createFileRoute('/_authLayout/debug/widget')({
  component: DebugWidget,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    return {
      portfolioId: search.portfolioId as string | undefined,
      snapshotId: search.snapshotId as string | undefined,
      widget: (search.widget as string | undefined) || 'asset-breakdown',
      scenario:
        (search.scenario as
          | 'proposed'
          | 'current_holdings'
          | 'benchmark'
          | 'universe') || 'proposed',
      horizonDays: search.horizonDays ? Number(search.horizonDays) : 63,
    };
  },
});

// Widget configs factory that accepts hasBenchmark parameter
const createWidgetConfigs = (hasBenchmark: boolean) => ({
  'asset-breakdown': {
    name: 'Asset Breakdown',
    component: AssetBreakdownWidget,
    fetchData: async (snapshotId: string, params: any) => {
      return await endpointPortfolioConstituentBreakdownGET({
        snapshot_id: snapshotId,
        scenario: params.scenario || 'PROPOSED',
        horizon_days: params.horizonDays || 10,
      });
    },
    defaultParams: { scenario: 'PROPOSED', horizonDays: 10 },
  },
  'forward-returns': {
    name: 'Forward Returns',
    component: ForwardReturnsWidget,
    fetchData: async (snapshotId: string, params: any) => {
      return await endpointPortfolioForwardProjectionGET({
        snapshot_id: snapshotId,
        horizon_days: params.horizonDays || 252,
        include_current: true,
        include_benchmark: hasBenchmark,
      });
    },
    defaultParams: { horizonDays: 252 },
  },
  'historical-returns': {
    name: 'Historical Returns',
    component: HistoricalReturnsWidget,
    fetchData: async (snapshotId: string, params: any) => {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 6);

      return await endpointPortfolioPerformanceGET({
        snapshot_id: snapshotId,
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        include_current: true,
        include_benchmark: hasBenchmark,
      });
    },
    defaultParams: {},
  },
});

function DebugWidget() {
  const {
    portfolioId,
    snapshotId,
    widget = 'asset-breakdown',
    scenario = 'proposed',
    horizonDays = 63,
  } = useSearch({
    from: '/_authLayout/debug/widget',
  });

  const [data, setData] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [actualSnapshotId, setActualSnapshotId] = useState<string | null>(null);

  // Check if portfolio has a benchmark using the dedicated endpoint
  const { hasBenchmark, isLoading: hasBenchmarkLoading } =
    useHasBenchmark(portfolioId);

  // Get latest snapshot ID
  const { data: snapshotData } = useQuery(GET_LATEST_SNAPSHOT_ID, {
    variables: { portfolioId },
    skip: !portfolioId,
  });

  const widgetConfigs = createWidgetConfigs(hasBenchmark);
  const widgetConfig = widgetConfigs[widget as keyof typeof widgetConfigs];

  // Fetch data for the selected widget
  const fetchData = useCallback(async () => {
    if (!snapshotId && !portfolioId) return;
    if (!widgetConfig) return;

    try {
      setIsLoading(true);
      setError(null);

      // Get latest snapshot if not provided
      let targetSnapshotId = snapshotId;
      if (
        !targetSnapshotId &&
        snapshotData?.EQPortfolio?.[0]?.EQPortfolioSnapshots?.[0]?.id
      ) {
        targetSnapshotId =
          snapshotData.EQPortfolio[0].EQPortfolioSnapshots[0].id;
      }

      if (!targetSnapshotId) {
        throw new Error('No snapshot ID available');
      }

      setActualSnapshotId(targetSnapshotId);

      const response = await widgetConfig.fetchData(targetSnapshotId, {
        scenario,
        horizonDays,
      });

      setData(response);
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  }, [
    snapshotId,
    portfolioId,
    snapshotData,
    widgetConfig,
    scenario,
    horizonDays,
  ]);

  useEffect(() => {
    // Wait for benchmark check to complete before fetching
    if (hasBenchmarkLoading) {
      return;
    }

    if (portfolioId && widgetConfig) {
      fetchData();
    }
  }, [portfolioId, fetchData, widgetConfig, hasBenchmarkLoading]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  if (!widgetConfig) {
    return (
      <div
        style={{
          padding: '20px',
          backgroundColor: '#1a1a1a',
          minHeight: '100vh',
        }}
      >
        <h1 style={{ color: 'white', marginBottom: '20px' }}>
          Unknown Widget: {widget}
        </h1>
        <div style={{ color: '#888' }}>
          <p>Available widgets:</p>
          <ul>
            {Object.keys(widgetConfigs).map((key) => (
              <li key={key}>{key}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  const WidgetComponent = widgetConfig.component;

  return (
    <div
      style={{
        padding: '20px',
        backgroundColor: '#1a1a1a',
        minHeight: '100vh',
      }}
    >
      <h1 style={{ color: 'white', marginBottom: '20px' }}>
        {widgetConfig.name} - Debug Page
      </h1>

      {!portfolioId ? (
        <div style={{ color: '#888' }}>
          <p>Add query parameters to load real data:</p>
          <p>?portfolioId=YOUR_PORTFOLIO_ID&widget=WIDGET_NAME</p>
          <p>
            Example:
            ?portfolioId=0199e3bb-61d5-7825-96b3-6d2cc0d96a9c&widget=asset-breakdown
          </p>
          <br />
          <p>
            <strong>Available widgets:</strong>
          </p>
          <ul>
            {Object.entries(widgetConfigs).map(([key, config]) => (
              <li key={key}>
                <strong>{key}</strong> - {config.name}
              </li>
            ))}
          </ul>
          <br />
          <p>
            <strong>Optional parameters:</strong>
          </p>
          <ul>
            <li>
              scenario: proposed | current_holdings | benchmark | universe (for
              asset-breakdown)
            </li>
            <li>horizonDays: number (projection horizon in days)</li>
          </ul>
        </div>
      ) : (
        <>
          <p style={{ color: '#888', marginBottom: '20px' }}>
            Portfolio ID: {portfolioId}
            <br />
            Snapshot ID: {snapshotId || 'latest'}
            <br />
            Widget: {widget}
            <br />
            Has Benchmark: {hasBenchmark ? 'Yes' : 'No'}
            {widget === 'asset-breakdown' && (
              <>
                <br />
                Scenario: {scenario}
              </>
            )}
            <br />
            Horizon Days: {horizonDays}
          </p>

          <div
            style={{
              height: '700px',
              backgroundColor: '#2a2a2a',
              padding: '20px',
            }}
          >
            <WidgetComponent
              data={data || null}
              isLoading={isLoading || hasBenchmarkLoading}
              error={error}
              refetch={refetch}
              widgetId={`debug-${widget}`}
              title={`${widgetConfig.name}`}
              snapshotId={actualSnapshotId}
              scenario={scenario}
              benchmarkName="Benchmark"
              {...(widgetConfig.defaultParams || {})}
            />
          </div>

          {error && (
            <div
              style={{
                marginTop: '20px',
                color: 'red',
                padding: '20px',
                backgroundColor: '#3a2a2a',
              }}
            >
              <h3>Error:</h3>
              <pre>{error.message}</pre>
              <pre>{error.stack}</pre>
            </div>
          )}

          {data && (
            <div style={{ marginTop: '20px', color: 'white' }}>
              <h3>Raw Data Preview:</h3>
              <pre
                style={{
                  maxHeight: '300px',
                  overflow: 'auto',
                  backgroundColor: '#2a2a2a',
                  padding: '10px',
                  fontSize: '12px',
                }}
              >
                {JSON.stringify(data, null, 2)}
              </pre>
            </div>
          )}

          {isLoading && (
            <div style={{ marginTop: '20px', color: 'yellow' }}>
              <p>Loading...</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
